import { z } from 'zod'
import { api } from '@/shared/api/client'
import { syntheticTotal } from '@/shared/api/pageResult'
import { moneySchema, toMoney } from '@/shared/api/schemas'
import { downloadServerExport } from '@/shared/lib/serverExport'
import type { Money } from '@/shared/lib/money'
import type { PageResult } from '@/shared/ui/table/DataTable'
import type { TableState } from '@/shared/ui/table/tableState'

export const HISTORY_KINDS = [
  'OPENING_BALANCE',
  'TOPUP',
  'PACKAGE_SALE',
  'SUBSCRIPTION_CHARGE',
  'DEVICE_RENTAL',
  'INVOICE_SALE',
  'DISCOUNT',
  'REFUND',
  'ADJUSTMENT',
  'REVERSAL',
  'USAGE_CHARGE',
  'AGENT_DEPOSIT_FUNDING',
  'COMMISSION',
  'CASH_HANDOVER',
] as const

export type HistoryKind = (typeof HISTORY_KINDS)[number] | 'OTHER'

export const ACTOR_TYPES = ['STAFF', 'AGENT', 'PSP', 'SYSTEM'] as const
export type ActorType = (typeof ACTOR_TYPES)[number]

const MAX_PAGE_SIZE = 200

const rowSchema = z.object({
  txnId: z.number(),
  at: z.string(),
  kind: z.string(),
  amount: moneySchema,
  balanceAfter: moneySchema,
  trafficBytes: z.number().nullish(),
  reference: z.string().nullish(),
  note: z.string().nullish(),
  packageName: z.string().nullish(),
  actor: z.object({ type: z.string(), name: z.string().nullish() }).nullish(),
})

const pageSchema = z.object({
  from: z.string(),
  to: z.string(),
  opening: moneySchema,
  closing: moneySchema,
  rows: z.array(rowSchema),
  page: z.number(),
  size: z.number(),
  hasNext: z.boolean(),
})

const statementSchema = z.object({
  company: z.object({ name: z.string().nullish(), logoUrl: z.string().nullish() }).nullish(),
  customer: z.object({ login: z.string(), name: z.string().nullish(), customerId: z.string().nullish() }),
  from: z.string(),
  to: z.string(),
  timezone: z.string(),
  opening: moneySchema,
  closing: moneySchema,
  rows: z.array(rowSchema),
  truncated: z.boolean(),
  generatedAt: z.string(),
})

export interface HistoryRow {
  id: string
  at: string
  kind: HistoryKind
  amount: Money
  balanceAfter: Money
  trafficBytes: number
  reference: string | null
  note: string | null
  packageName: string | null
  actor: { type: ActorType | 'OTHER'; name: string | null } | null
}

export interface HistorySummary {
  from: string
  to: string
  opening: Money
  closing: Money
}

export interface Statement extends HistorySummary {
  company: { name: string | null; logoUrl: string | null }
  customer: { login: string; name: string | null; customerId: string | null }
  timezone: string
  rows: HistoryRow[]
  truncated: boolean
  generatedAt: string
}

export interface HistoryPeriod {
  from: string
  to: string
}

function toKind(raw: string): HistoryKind {
  return (HISTORY_KINDS as readonly string[]).includes(raw) ? (raw as HistoryKind) : 'OTHER'
}

function toRow(raw: z.infer<typeof rowSchema>): HistoryRow {
  return {
    id: String(raw.txnId),
    at: raw.at,
    kind: toKind(raw.kind),
    amount: toMoney(raw.amount),
    balanceAfter: toMoney(raw.balanceAfter),
    trafficBytes: raw.trafficBytes ?? 0,
    reference: raw.reference ?? null,
    note: raw.note ?? null,
    packageName: raw.packageName ?? null,
    actor: raw.actor
      ? {
          type: (ACTOR_TYPES as readonly string[]).includes(raw.actor.type)
            ? (raw.actor.type as ActorType)
            : 'OTHER',
          name: raw.actor.name ?? null,
        }
      : null,
  }
}

export function fetchHistoryPage(
  subscriberId: string,
  period: HistoryPeriod,
  onSummary: (summary: HistorySummary) => void,
): (state: TableState, signal: AbortSignal) => Promise<PageResult<HistoryRow>> {
  return async (state, signal) => {
    const response = await api.get<unknown>(`/api/subscribers/${subscriberId}/history`, {
      signal,
      params: {
        from: period.from,
        to: period.to,
        page: state.page - 1,
        size: Math.min(state.pageSize, MAX_PAGE_SIZE),
      },
    })
    const parsed = pageSchema.parse(response.data)
    onSummary({
      from: parsed.from,
      to: parsed.to,
      opening: toMoney(parsed.opening),
      closing: toMoney(parsed.closing),
    })
    return {
      items: parsed.rows.map(toRow),
      total: syntheticTotal(state.page, state.pageSize, parsed.rows.length, parsed.hasNext),
    }
  }
}

export async function fetchStatement(subscriberId: string, period: HistoryPeriod): Promise<Statement> {
  const response = await api.get<unknown>(`/api/subscribers/${subscriberId}/history/statement`, {
    params: period,
  })
  const parsed = statementSchema.parse(response.data)
  return {
    from: parsed.from,
    to: parsed.to,
    opening: toMoney(parsed.opening),
    closing: toMoney(parsed.closing),
    company: { name: parsed.company?.name ?? null, logoUrl: parsed.company?.logoUrl ?? null },
    customer: {
      login: parsed.customer.login,
      name: parsed.customer.name ?? null,
      customerId: parsed.customer.customerId ?? null,
    },
    timezone: parsed.timezone,
    rows: parsed.rows.map(toRow),
    truncated: parsed.truncated,
    generatedAt: parsed.generatedAt,
  }
}

export async function downloadHistoryExport(
  subscriberId: string,
  login: string,
  period: HistoryPeriod,
  format: string,
  onProgress: (loaded: number) => void,
): Promise<void> {
  await downloadServerExport({
    path: `/api/subscribers/${subscriberId}/history/export`,
    format,
    params: { from: period.from, to: period.to },
    fallbackBaseName: `subscriber-history-${login}-${period.from}_${period.to}`,
    onProgress,
  })
}
